import React, { useCallback, useState, type ReactNode, useEffect } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import EditorSettingsIcon from '@atlaskit/icon/core/migration/settings--editor-settings';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { Inline } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { QUEUE_ROUTES } from '@atlassian/jira-servicedesk-common/src/navigation/queues/index.tsx';
import { useIsGettingStartedPageEnabled } from '@atlassian/jira-servicedesk-getting-started-page-common/src/common/controllers/getting-started-page-enabled/useIsGettingStartedPageEnabled.tsx';
import {
	useManageQueuesDialog,
	useIsManageQueuesDialogVisible,
} from '@atlassian/jira-servicedesk-queues-manage-dialog/src/controllers/index.tsx';
import type { ItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import {
	CURATE_PRIORITY_GROUPS_FOR_YOUR_TEAMS_ENGAGEMENT_ID,
	GETTING_STARTED_REFERRER_KEY,
	GETTING_STARTED_REFERRER_VALUE,
} from './constants.tsx';
import messages from './messages.tsx';

type ManageQueuesItemProps = {
	children: ReactNode;
	practice: ItsmPractice;
};

export const ManageQueuesItem = ({ children, practice }: ManageQueuesItemProps) => {
	const [, { showManageQueuesDialog: showManageQueuesDialogFromHook }] = useManageQueuesDialog();
	const [isManageQueuesDialogVisible] = useIsManageQueuesDialogVisible();
	const [, setShowPulse] = useState(true);
	const { data: project } = useProject();
	const projectKey = project ? project.key : '';
	const [isLoading, isGettingStartedPageEnabled] = useIsGettingStartedPageEnabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();

	const [referrer, setReferrer] = useQueryParam(GETTING_STARTED_REFERRER_KEY);

	const showManageQueuesDialog = useCallback(() => {
		if (!isManageQueuesDialogVisible) {
			showManageQueuesDialogFromHook();
			setShowPulse(false);
			setReferrer(undefined);
		}
	}, [isManageQueuesDialogVisible, setReferrer, showManageQueuesDialogFromHook]);

	useEffect(() => {
		const isReferrerValue = referrer === GETTING_STARTED_REFERRER_VALUE;

		if (isReferrerValue && !isLoading && isGettingStartedPageEnabled) {
			showManageQueuesDialog();
		}
	}, [
		isGettingStartedPageEnabled,
		isLoading,
		isManageQueuesDialogVisible,
		referrer,
		showManageQueuesDialog,
	]);

	useEffect(() => {
		// We need to make sure queue settings menu item is rendered before trigger event
		// that will cause queue settings Engagement Platform message to be displayed,
		// otherwise the message will be displayed in the wrong position.
		setTimeout(() => {
			fireTrackAnalytics(createAnalyticsEvent({}), 'queueSettings rendered', {
				meta: {
					packageName: 'jiraNavigationAppsSidebarServicedesk',
					teamName: 'jsd-shield',
				},
			});
		}, 1500);
	}, [createAnalyticsEvent]);

	return (
		<SpotlightManager>
			<SpotlightTarget name={CURATE_PRIORITY_GROUPS_FOR_YOUR_TEAMS_ENGAGEMENT_ID}>
				<MenuItem
					analytics={{ itemId: 'manage page' }}
					href={`/jira/servicedesk/projects/${projectKey}/${QUEUE_ROUTES[practice]}/queue-settings`}
					selectedOn={matchUrlPathname()}
					iconBefore={<EditorSettingsIcon label="" LEGACY_size="medium" spacing="spacious" />}
				>
					<Inline spread="space-between">
						{children}
						<Lozenge appearance="new">{formatMessage(messages.itemManageQueuesNewLozenge)}</Lozenge>
					</Inline>
				</MenuItem>
			</SpotlightTarget>
			<EngagementSpotlight engagementId={CURATE_PRIORITY_GROUPS_FOR_YOUR_TEAMS_ENGAGEMENT_ID} />
		</SpotlightManager>
	);
};
